import { useCallback, useEffect, useState } from "react";
import { relativeTimeFrom } from "./server-time";
import { useClickOutsideToClose } from "./ui-kit";

export type AppNotification = {
  body: string | null;
  category: string;
  createdAt: string;
  id: string;
  readAt: string | null;
  taskKey: string | null;
  title: string;
};

type NotificationList = { notifications: AppNotification[]; unreadCount: number };

const REFRESH_MS = 60_000;

const request = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json" } : undefined,
  });
  if (!response.ok) throw new Error("Notifications are unavailable.");
  return (await response.json()) as T;
};

// Refreshes every `intervalMs`, but only while the tab is visible: every
// refresh costs D1 rows read, and a tab left open in the background all day
// used to poll 1,440 times for a badge nobody was looking at. Coming back to
// the tab (or the window) refreshes at once, so the badge is current when it
// is seen. A tab switch fires both `visibilitychange` and `focus`; the
// second one within `minGapMs` is skipped. Returns the cleanup function.
export const startNotificationPolling = (
  { doc, intervalMs, minGapMs = 5_000, now = Date.now, refresh, win }: {
    doc: Pick<Document, "addEventListener" | "hidden" | "removeEventListener">;
    intervalMs: number;
    minGapMs?: number;
    now?: () => number;
    refresh: () => void;
    win: Pick<Window, "addEventListener" | "clearInterval" | "removeEventListener" | "setInterval">;
  },
) => {
  let lastRefreshAt = Number.NEGATIVE_INFINITY;
  const run = () => {
    lastRefreshAt = now();
    refresh();
  };
  const onTick = () => {
    if (!doc.hidden) run();
  };
  const onReturn = () => {
    if (!doc.hidden && now() - lastRefreshAt >= minGapMs) run();
  };
  run();
  const timer = win.setInterval(onTick, intervalMs);
  doc.addEventListener("visibilitychange", onReturn);
  win.addEventListener("focus", onReturn);
  return () => {
    win.clearInterval(timer);
    doc.removeEventListener("visibilitychange", onReturn);
    win.removeEventListener("focus", onReturn);
  };
};

// Where a click on a notification goes: the task it is about, if it has one.
export const notificationHref = (notification: Pick<AppNotification, "taskKey">) =>
  notification.taskKey ? `/t/${encodeURIComponent(notification.taskKey)}` : null;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<NotificationList>({ notifications: [], unreadCount: 0 });
  const [error, setError] = useState("");
  const ref = useClickOutsideToClose<HTMLDivElement>(open, () => setOpen(false));

  const refresh = useCallback(async () => {
    try {
      setList(await request<NotificationList>("/api/notifications"));
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Notifications are unavailable.");
    }
  }, []);

  useEffect(() => startNotificationPolling({
    doc: document, intervalMs: REFRESH_MS, refresh: () => void refresh(), win: window,
  }), [refresh]);

  const markRead = async (body: { all: true } | { ids: string[] }) => {
    try {
      await request("/api/notifications/read", { body: JSON.stringify(body), method: "POST" });
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Notifications are unavailable.");
    }
  };

  const openNotification = async (notification: AppNotification) => {
    const href = notificationHref(notification);
    if (!notification.readAt) await markRead({ ids: [notification.id] });
    if (href) window.location.assign(href);
  };

  const unread = list.unreadCount;
  const label = unread > 0 ? `Notifications, ${unread} unread` : "Notifications";
  return (
    <div className="notification-bell" ref={ref}>
      <button
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={label}
        className="chrome-icon-button"
        onClick={() => { setOpen((value) => !value); if (!open) void refresh(); }}
        title="Notifications"
        type="button"
      >
        <i aria-hidden="true" className="bi bi-bell" />
        {unread > 0 && <span aria-hidden="true" className="notification-badge">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div aria-label="Notifications" className="notification-panel" role="dialog">
          <header>
            <strong>Notifications</strong>
            <button disabled={unread === 0} onClick={() => void markRead({ all: true })} type="button">Mark all as read</button>
          </header>
          {error && <p role="alert">{error}</p>}
          {!error && list.notifications.length === 0 && <p>You have no notifications yet.</p>}
          <ul>
            {list.notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  className={notification.readAt ? "" : "unread"}
                  data-read={notification.readAt ? "true" : "false"}
                  onClick={() => void openNotification(notification)}
                  type="button"
                >
                  <strong>{notification.title}</strong>
                  {notification.body && <span>{notification.body}</span>}
                  <small>{relativeTimeFrom(notification.createdAt)}</small>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
